import { useEffect, useSyncExternalStore } from 'react';

/**
 * Which user, if any, has a password-recovery session in hand — a session established
 * by the emailed reset link, as opposed to an ordinary sign-in.
 *
 * The store holds the recovering user's id, not a bare flag, so the reset screen can
 * check that the recovery session and the session it is about to change the password
 * on are the SAME account. A boolean would outlive its session: established, abandoned,
 * signed out of, signed back in as someone else — and still authorize a password change
 * on that second account, in the same app process, with no email round-trip.
 *
 * This is its own module — with no Supabase import — so `supabase.ts` can mark it
 * the instant a `PASSWORD_RECOVERY` event fires without an import cycle. It exists
 * because the event cannot be observed from the reset screen: auth-js emits
 * `PASSWORD_RECOVERY` once, from inside `_initialize()` at client construction, and
 * then clears the URL hash — long before `/reset/new-password` mounts and could
 * subscribe. `supabase.ts` registers the listener synchronously after `createClient`
 * (the only place guaranteed to be in time) and records the fact here, where the
 * screen can read it back.
 */
let recoveringUserId: string | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/** Record that `userId`'s current session came from the password-recovery flow. */
export function markRecoverySession(userId: string) {
  if (recoveringUserId === userId) return;
  recoveringUserId = userId;
  emit();
}

/**
 * Clear the recovery marker. Called after a successful password change so the mark is
 * single-use and cannot authorize a second change later in the same app session, and
 * on sign-out so it cannot outlive the session it was established for.
 */
export function consumeRecoverySession() {
  if (recoveringUserId === null) return;
  recoveringUserId = null;
  emit();
}

/** The user id of the recovery session in hand, or null if there isn't one. */
export function getRecoverySnapshot(): string | null {
  return recoveringUserId;
}

/** Subscribe to recovery-store changes; returns an unsubscribe. */
export function subscribeRecoverySession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** React binding for {@link getRecoverySnapshot}. */
export function useRecoverySession(): string | null {
  return useSyncExternalStore(subscribeRecoverySession, getRecoverySnapshot);
}

let pendingRelease: ReturnType<typeof setTimeout> | null = null;

/**
 * Consume the recovery mark when the calling screen unmounts, so an abandoned
 * recovery (link followed, password never set, screen navigated away from) does not
 * leave the route armed for the rest of the app's life.
 *
 * The release is deferred a tick rather than run in the cleanup itself: StrictMode's
 * dev-only remount runs setup → cleanup → setup synchronously, and an immediate
 * consume there would eat a fresh mark on first render and brick the flow. The
 * re-setup cancels the pending release; a real unmount has no second setup, so the
 * timer fires and the mark dies with the screen it authorized.
 */
export function useConsumeRecoveryOnUnmount() {
  useEffect(() => {
    if (pendingRelease !== null) {
      clearTimeout(pendingRelease);
      pendingRelease = null;
    }
    return () => {
      pendingRelease = setTimeout(() => {
        pendingRelease = null;
        consumeRecoverySession();
      }, 0);
    };
  }, []);
}
